<script lang="ts">
	import { page } from '$app/state';
	import { resolve } from '$app/paths';
	import MarketingSeoHead from '$lib/components/marketing/MarketingSeoHead.svelte';
	import { m } from '$lib/paraglide/messages.js';
	import { absoluteUrl, faqStructuredData } from '$lib/utils/marketing-seo';
	import Button from '@/components/ui/button/button.svelte';
	import { getLocale, localizeHref } from '@/paraglide/runtime';
	import CirclePlus from '@lucide/svelte/icons/circle-plus';
	import Search from '@lucide/svelte/icons/search';
	import Sailboat from '@lucide/svelte/icons/sailboat';
	import Waves from '@lucide/svelte/icons/waves';

	const locale = $derived(getLocale());

	function formatAnswer(text: string): string {
		text = text.replace(
			/^>\s+(.+)$/gm,
			'<blockquote class="border-l-4 border-gray-300 pl-4 italic my-2">$1</blockquote>'
		);

		const lines = text.split('\n');
		const processedLines: string[] = [];
		let inList = false;
		let listItems: string[] = [];

		for (const line of lines) {
			const listMatch = line.match(/^\*\s+(.+)$/);

			if (listMatch) {
				if (!inList) {
					inList = true;
					listItems = [];
				}
				listItems.push(listMatch[1]);
			} else {
				if (inList) {
					processedLines.push(
						`<ul class="list-disc list-inside space-y-1 my-2">${listItems.map((item) => `<li>${item}</li>`).join('')}</ul>`
					);
					listItems = [];
					inList = false;
				}
				processedLines.push(line);
			}
		}

		if (inList && listItems.length > 0) {
			processedLines.push(
				`<ul class="list-disc list-inside space-y-1 my-2">${listItems.map((item) => `<li>${item}</li>`).join('')}</ul>`
			);
		}

		text = processedLines.join('\n');
		text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
		text = text.replace(
			/\[([^\]]+)\]\(([^)]+)\)/g,
			'<a href="$2" class="text-blue-600 hover:text-blue-800 underline">$1</a>'
		);
		text = text.replace(/\n\n/g, '<br><br>');
		text = text.replace(/\n/g, '<br>');

		return text;
	}

	const faqItemsBase = [
		{
			question: '📜 ¿La nueva ley está en vigor?',
			answer:
				'Sí. **Ya está en vigor** desde el 15 de agosto y puedes tramitar el **cambio de uso** desde hoy mismo.'
		},
		{
			question: '⚓ ¿Qué tengo que hacer para alquilar mi barco? (2 pasos)',
			answer:
				'**1. Cambio de uso** → tu barco pasa a "alquilable".\n\nRequisitos: **contrato Armador–Zarpar**, **tasa de Capitanía** y **DNI**.\n\nSe puede solicitar **ya** y tiene **vigencia de 5 años**.\n\n**2. Despacho temporal** → lo activas cuando decidas tus **3 meses consecutivos** de alquiler.\n\nEn ese momento necesitas **seguro ampliado**, **ITB intermedia** y **identificador CT**.\n\n> Te guiamos paso a paso.\n\n> 📩 [Contacto](__CONTACT__)'
		},
		{
			question: '⏳ ¿Cuánto tiempo puedo alquilarlo?',
			answer: 'Hasta **3 meses seguidos** cada año.'
		},
		{
			question: '💶 ¿Con cuántos alquileres cubro mis gastos?',
			answer:
				'De media, **unos 20 días de alquiler** cubren:\n\n**amarre + varada anual + seguro**.'
		},
		{
			question: '🧭 ¿Quién lleva el barco?',
			answer:
				'Tú decides:\n\n* **Experiencias** → lo llevas tú (gastos compartidos).\n* **Alquiler** → lo lleva el inquilino con título o un **patrón profesional**.'
		},
		{
			question: '☀️ ¿Necesito algún trámite para ofrecer experiencias?',
			answer:
				'No. **Ninguno.**\n\nNo requieren cambio de uso, ni despacho, ni ITB extra, ni seguro adicional.\n\nPuedes **crear experiencias desde YA**. Solo necesitas el barco en buen estado, tu titulación.'
		},
		{
			question: '🛥️ ¿Cómo subo mi barco y publico mi anuncio?',
			answer:
				'Es muy simple:\n\n1. **Sube tu barco** → datos básicos y fotos.\n2. **Crea tu anuncio** → texto corto y condiciones.\n3. **Elige modalidad** → *Experiencia* (ya) o *Alquiler* (cuando actives los 3 meses).\n4. **Publica** → empiezas a recibir solicitudes.'
		},
		{
			question: '👨‍✈️ ¿De dónde salen los patrones profesionales?',
			answer:
				'Estamos creando una **bolsa de capitanes**.\n\nMientras tanto, puedes traer el tuyo o **hablar con nosotros**.\n\n📩 [Contacto](__CONTACT__)'
		},
		{
			question: '🛡️ ¿Qué seguro necesito?',
			answer:
				'Si haces alquileres, tu póliza debe incluir:\n\n* **Uso de alquiler (arrendamiento náutico)**\n* **SOVI (Seguro Obligatorio de Viajeros)**\n\nNosotros te guiamos con todo.'
		},
		{
			question: '🧾 ¿Cuánto cuesta la ITB?',
			answer: 'La **ITB intermedia** suele costar entre **150–350 €**, según eslora.'
		},
		{
			question: '📝 ¿Cómo funciona el check-in / check-out?',
			answer:
				'Puede hacerlo:\n\n* Tú\n* El patrón\n* O nosotros si estás en Vigo\n\n📩 [Contacto](__CONTACT__)\n\nUsamos checklists claros y recomendamos hacer fotos.'
		},
		{
			question: '↩️ ¿Cuál es la política de cancelación?',
			answer:
				'Puedes elegir entre:\n\n* **Flexible** → gratis hasta **2 días antes**\n* **Estricta** → gratis hasta **15 días antes**'
		},
		{
			question: '💸 ¿Qué comisión aplica Zarpar?',
			answer:
				'Zarpar aplica una **comisión del 15% + IVA sobre el precio de la reserva**.\n\n**Todos los servicios de gestión, documentación y asesoramiento están incluidos** sin coste adicional para el armador.'
		},
		{
			question: '🔐 ¿Es seguro el sistema de pagos?',
			answer:
				'Sí. Utilizamos **Stripe**, la plataforma de pagos más usada del mundo (Airbnb, Booking, Shopify…).\n\nEl dinero llega **directamente** a tu cuenta bancaria.'
		},
		{
			question: '📅 ¿Cuándo cobro las reservas?',
			answer:
				'Realizamos una **transferencia mensual** con el dinero acumulado de todas tus reservas.\n\nPara que una reserva se incluya en el pago del mes, debe haber finalizado **al menos 7 días antes** de la fecha de pago. Si una reserva termina menos de 7 días antes, el dinero estará disponible en el pago del mes siguiente.'
		},
		{
			question: '🛥️ ¿Puedo usar mi barco fuera de los meses de alquiler?',
			answer:
				'Sí.\n\nSolo durante los **3 meses despachados** no puedes usarlo como particular (con algunas excepciones).'
		},
		{
			question: '🌅 ¿Puedo ofrecer experiencias sin cambiar de uso?',
			answer: 'Sí. Son **gastos compartidos** y no necesitan trámites.'
		},
		{
			question: '🛟 ¿Mi barco vale para esto?',
			answer: 'Cualquier barco de **lista 7ª**, en buen estado y con seguro e ITB, puede hacerlo.'
		},
		{
			question: '🤝 ¿Zarpar gestiona los trámites por mí?',
			answer:
				'Sí.\n\nNos encargamos del **cambio de uso**, el **despacho**, las **plantillas**, y te acompañamos durante todo el proceso.\n\n📩 [Contacto](__CONTACT__)'
		}
	] as const;

	const contactUrl = $derived(absoluteUrl(localizeHref('/contacto')));

	const faqItems = $derived.by(() => {
		void locale;
		return faqItemsBase.map((item) => ({
			...item,
			answer: item.answer.replaceAll('__CONTACT__', contactUrl)
		}));
	});

	const seoMeta = $derived.by(() => {
		void locale;
		return {
			title: m.faq_meta_title(),
			description: m.faq_meta_description()
		};
	});

	const faqJsonLd = $derived.by(() => {
		void locale;
		return faqStructuredData({
			title: seoMeta.title,
			description: seoMeta.description,
			pageUrl: absoluteUrl(page.url.pathname),
			items: faqItems
		});
	});
</script>

<MarketingSeoHead meta={seoMeta} canonicalPath="/preguntas-frecuentes" structuredData={faqJsonLd} />

<main class="bg-white">
	<div class="mx-auto max-w-7xl divide-y divide-gray-900/10 px-6 py-8 sm:py-12 lg:px-24">
		<header class="mb-8">
			<h1 class="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">{m.faq_h1()}</h1>
		</header>

		<div class="mb-8 rounded-lg border border-blue-200 bg-blue-50/50 p-6">
			<div class="mb-4 text-center">
				<h2 class="text-lg font-semibold text-gray-900">{m.faq_intro_h3()}</h2>
				<p class="mt-2 text-sm text-gray-600">
					{m.faq_intro_p()}
				</p>
			</div>
			<div class="grid gap-4 sm:grid-cols-2">
				<a
					href={resolve(localizeHref('/alquiler-lista-septima'))}
					class="flex items-start gap-3 rounded-lg border border-blue-100 bg-white p-4 transition-colors hover:border-blue-300 hover:bg-blue-50/50"
				>
					<div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-100">
						<Sailboat class="h-5 w-5 text-blue-600" />
					</div>
					<div class="flex-1">
						<h3 class="font-semibold text-gray-900 hover:text-blue-600">
							{m.faq_mod_rental_title()}
						</h3>
						<p class="mt-1 text-sm text-gray-600">
							{m.faq_mod_rental_desc()}
						</p>
					</div>
				</a>
				<a
					href={resolve(localizeHref('/zarpar-experiencias'))}
					class="flex items-start gap-3 rounded-lg border border-blue-100 bg-white p-4 transition-colors hover:border-blue-300 hover:bg-blue-50/50"
				>
					<div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-100">
						<Waves class="h-5 w-5 text-blue-600" />
					</div>
					<div class="flex-1">
						<h3 class="font-semibold text-gray-900 hover:text-blue-600">{m.faq_mod_exp_title()}</h3>
						<p class="mt-1 text-sm text-gray-600">
							{m.faq_mod_exp_desc()}
						</p>
					</div>
				</a>
			</div>
		</div>

		<dl class="mt-10 space-y-8 divide-y divide-gray-900/10">
			{#each faqItems as item (item.question)}
				<div class="pt-8 lg:grid lg:grid-cols-12 lg:gap-8">
					<dt class="text-base leading-7 font-semibold text-gray-900 lg:col-span-5">
						{item.question}
					</dt>
					<dd class="mt-4 lg:col-span-7 lg:mt-0">
						<div class="text-base leading-7 text-gray-600">
							{@html formatAnswer(item.answer)}
						</div>
					</dd>
				</div>
			{/each}
		</dl>

		<div class="mt-16 border-t border-gray-200 pt-12">
			<div class="text-center">
				<h2 class="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
					{m.faq_cta_h3()}
				</h2>
				<p class="mx-auto mt-4 max-w-2xl text-lg text-gray-600">
					{m.faq_cta_p()}
				</p>
				<div class="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
					<Button size="lg" class="group" href={localizeHref('/barcos')}>
						<Search class="mr-2 h-5 w-5 transition-transform group-hover:scale-110" />
						{m.faq_cta_browse()}
					</Button>
					<Button size="lg" variant="outline" class="group" href={localizeHref('/anadir-barco')}>
						<CirclePlus class="mr-2 h-5 w-5 transition-transform group-hover:rotate-90" />
						{m.faq_cta_add_boat()}
					</Button>
				</div>
			</div>
		</div>
	</div>
</main>
